import {Time, TypeUtil} from 'yougile-util'
import {
  DataObjectId,
  Hub,
  JSONLikeValue,
  noBoardContainerId,
  Subtask,
  taskTemplatesContainerId,
  TimerStickerEntry
} from 'yougile-model'
import {Sync} from '@client/sync'
import {globals} from '@extensions/globals-base'
import {SeenBy} from '@extensions/seen-by'
import {Sticker} from '@extensions/sticker/sticker'
import {filterCustomStickers} from './filter-custom-stickers'

export const recreateSubtasksForTaskTemplate = (newTask: Hub): void => {
  const box = Sync.getCurrentSandbox()
  const companyId = box.companyId

  const subtasksList = newTask.data?.subtaskList
  if (!subtasksList) {
    return
  }

  const output: Hub[] = []

  const subtasks = Object.values(subtasksList.subtasks)

  const subtasksAcc = {} as Record<string, Subtask>

  for (const subtask of subtasks) {
    const oldSubtaskId = subtask.id

    if (subtask.isDeleted) {
      continue
    }

    if (
      subtask.location !== noBoardContainerId &&
      subtask.location !== taskTemplatesContainerId
    ) {
      subtasksAcc[oldSubtaskId] = subtask
      continue
    }

    const subtaskHubOld = box.getHub(oldSubtaskId)

    if (!subtaskHubOld) {
      continue
    }

    const json = subtaskHubOld.toJSON()

    const hub = new Hub(companyId, '')
    hub.fromJSON(box.companyId, json)

    hub.data.subtaskList = {subtasks: {}, order: []}

    clearHub({task: hub, key: 'id', companyId})
    clearHub({task: hub, key: 'timestamp', companyId})
    clearHub({task: hub, key: 'chat', companyId})

    const tags = Object.keys(hub.stickers)
      .map(sId => {
        return Sticker.getById(box, sId as DataObjectId)
      })
      .filter(TypeUtil.isTruthy)

    const filteredIds = filterCustomStickers(tags, true).map(s => s.id)

    hub.stickers = (Object.keys(hub.stickers) as DataObjectId[]).reduce(
      (acc, stickerId) => {
        if (filteredIds.includes(stickerId)) {
          acc[stickerId] = hub.stickers[stickerId]
        }

        return acc
      },
      {} as {[id: string]: JSONLikeValue}
    )

    const deadlineTag = tags.find(t => t.title === 'Deadline')
    if (deadlineTag) {
      hub.stickers[deadlineTag.id] = ''
    }

    const timerTag = tags.find(t => t.title === 'Timer')
    if (timerTag) {
      const time = hub.stickers[timerTag.id]?.current?.time
      if (time) {
        const entry: TimerStickerEntry = {
          by: globals.currentUserId,
          running: false,
          time,
          timestamp: Time.now()
        }
        hub.stickers[timerTag.id] = {
          current: entry,
          history: [entry]
        }
      } else {
        hub.stickers[timerTag.id] = ''
      }
    }

    const newSubtaskId = hub.id

    const orderIdx = subtasksList.order.findIndex(item => item === oldSubtaskId)
    subtasksList.order[orderIdx] = newSubtaskId

    subtask.id = newSubtaskId
    subtask.isCompleted = false
    subtask.isArchived = undefined

    output.push(hub)

    subtasksAcc[newSubtaskId] = subtask
  }

  subtasksList.subtasks = subtasksAcc

  for (const hub of output) {
    box.addPhantom(hub, taskTemplatesContainerId)

    if (!SeenBy.isSeen(hub, globals.currentUserId)) {
      SeenBy.markSeen(hub, globals.currentUserId)
    }
  }
}

interface ClearProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  task: any // HubJSON
  key: keyof Hub
  companyId: DataObjectId
}

export const clearHub = (props: ClearProps): void => {
  const {task, key, companyId} = props
  task[key] = new Hub(companyId, '')[key]
}
